import type { StateCreator } from "../store";
import type { AppStore } from "../index";
import { HiLoState, type HiLoEnvironment } from "@/core_bridge/module_api";
import { db as storage } from "@/services/module_api";
import { openDatabase } from "@/services/module_api";
import { stateServices } from "@/state/module_api";

// Re-export for backward compatibility
export type HiloEnvironment = HiLoEnvironment | "";

// Storage key for encrypted hilo state
const HILO_STATE_KEY = "hilo:state";

// Global HiLoState instance (persisted to encrypted storage)
let hiloStateInstance: HiLoState | null = null;
let loadPromise: Promise<void> | null = null;

function getHiLoState(): HiLoState {
  if (!hiloStateInstance) {
    console.log("[hilo] Creating new HiLoState instance...");
    try {
      hiloStateInstance = new HiLoState();
      console.log("[hilo] HiLoState instance created successfully");
    } catch (e: any) {
      console.error("[hilo] Failed to create HiLoState:", e?.message);
      throw e;
    }
  }
  return hiloStateInstance;
}

async function ensureStorageOpen(): Promise<void> {
  // This initializes WASM and opens the encrypted storage database
  await openDatabase();
}

async function loadHiLoStateFromStorage(): Promise<void> {
  if (loadPromise) {
    console.log("[hilo] loadHiLoStateFromStorage: returning existing promise");
    return loadPromise;
  }

  console.log("[hilo] loadHiLoStateFromStorage: starting load...");
  loadPromise = (async () => {
    try {
      console.log("[hilo] loadHiLoStateFromStorage: ensuring storage is open...");
      await ensureStorageOpen();
      console.log("[hilo] loadHiLoStateFromStorage: getting stored value...");
      const stored = await storage.getSetting(HILO_STATE_KEY);
      console.log("[hilo] loadHiLoStateFromStorage: stored value:", stored ? `${stored.length} chars` : "null");
      if (stored) {
        try {
          hiloStateInstance = HiLoState.fromJSON(stored);
          console.log("[hilo] Restored state from encrypted storage, isConfigured:", hiloStateInstance.isConfigured);
        } catch (e) {
          console.warn("[hilo] Failed to restore state:", e);
          hiloStateInstance = new HiLoState();
        }
      } else {
        console.log("[hilo] No stored state found, creating new instance");
        hiloStateInstance = new HiLoState();
      }
    } catch (e) {
      console.warn("[hilo] Failed to load from storage:", e);
      hiloStateInstance = new HiLoState();
    }
  })();

  return loadPromise;
}

async function persistHiLoState(): Promise<void> {
  if (!hiloStateInstance) return;

  try {
    await ensureStorageOpen();
    await storage.setSetting(HILO_STATE_KEY, hiloStateInstance.toJSON());
    console.log("[hilo] State persisted to encrypted storage");
  } catch (e) {
    console.error("[hilo] Failed to persist state:", e);
  }
}

export interface HiloSlice {
  // State
  hi: string; // Kept for backward compat but not used for verification
  lo: string;
  destroy: string;
  environment: HiloEnvironment;
  isLocked: boolean;
  isConfigured: boolean;

  // Actions
  setHi: (code: string) => void;
  setLo: (code: string) => void;
  setDestroy: (code: string) => void;
  setEnvironment: (env: HiloEnvironment) => void;
  setCodes: (codes: { hi: string; lo: string; destroy?: string }) => void;
  loadCodes: () => void;
  saveCodes: () => void;

  // New lock screen actions
  lock: () => void;
  unlock: (code: string) => HiLoEnvironment | null;
  shouldLock: () => boolean;
}

export const createHiloSlice: StateCreator<AppStore, unknown, unknown, HiloSlice> = (set, get) => ({
  // Initial state
  hi: "",
  lo: "",
  destroy: "",
  environment: "",
  isLocked: false,
  isConfigured: false,

  // Actions
  setHi: (code) =>
    set((state) => {
      state.hi = code;
    }),

  setLo: (code) =>
    set((state) => {
      state.lo = code;
    }),

  setDestroy: (code) =>
    set((state) => {
      state.destroy = code;
    }),

  setEnvironment: (env) => {
    set((state) => {
      state.environment = env;
    });
    void stateServices.sessionState.setHiLoEnvironment(env || null);
    if (env === "hi" || env === "lo") {
      ensureStorageOpen().then(() => {
        const hiloState = getHiLoState();
        hiloState.setEnvironment(env);
        persistHiLoState();
      });
    }
  },

  setCodes: ({ hi, lo, destroy }) => {
    console.log("[hilo] setCodes called with:", { hi: hi?.length, lo: lo?.length, destroy: destroy?.length });

    // Ensure WASM is initialized before creating HiLoState
    const doSetCodes = async () => {
      try {
        console.log("[hilo] Ensuring WASM is initialized...");
        await ensureStorageOpen();
        console.log("[hilo] WASM initialized, getting HiLoState...");

        const hiloState = getHiLoState();
        console.log("[hilo] Got HiLoState instance:", hiloState);

        console.log("[hilo] Calling hiloState.setCodes...");
        hiloState.setCodes(hi, lo, destroy);
        console.log("[hilo] setCodes succeeded, persisting...");
        await persistHiLoState();

        set((state) => {
          state.hi = hi;
          state.lo = lo;
          if (destroy !== undefined) {
            state.destroy = destroy;
          }
          state.isConfigured = true;
        });
        void stateServices.sessionState.setHiLoEnabled(true);
        console.log("[hilo] Codes configured successfully");
      } catch (e: any) {
        console.error("[hilo] Failed to set codes:", e);
        console.error("[hilo] Error message:", e?.message);
        console.error("[hilo] Error stack:", e?.stack);
      }
    };

    doSetCodes();
  },

  loadCodes: () => {
    console.log("[hilo] loadCodes called, starting async load...");
    // Start async load - state will be updated when complete
    loadHiLoStateFromStorage().then(() => {
      const hiloState = getHiLoState();
      console.log("[hilo] loadCodes: loaded state:", {
        isConfigured: hiloState.isConfigured,
        isLocked: hiloState.isLocked,
        environment: hiloState.environment,
      });
      set((state) => {
        state.isConfigured = hiloState.isConfigured;
        state.isLocked = hiloState.isLocked;
        state.environment = hiloState.isConfigured ? hiloState.environment : "";
      });
      console.log("[hilo] loadCodes: state updated");
    }).catch((e) => {
      console.error("[hilo] loadCodes failed:", e);
    });
  },

  saveCodes: () => {
    persistHiLoState();
  },

  // Lock screen actions
  lock: () => {
    ensureStorageOpen().then(() => {
      const hiloState = getHiLoState();
      hiloState.lock();
      persistHiLoState();
      set((state) => {
        state.isLocked = true;
      });
      stateServices.sessionState.setHiLoUnlocked(false);
      console.log("[hilo] Screen locked");
    });
  },

  unlock: (code: string) => {
    // Note: This is sync for now but WASM should already be initialized by loadCodes
    try {
      const hiloState = getHiLoState();
      const env = hiloState.unlock(code);
      persistHiLoState();
      set((state) => {
        state.isLocked = false;
        state.environment = env;
      });
      stateServices.sessionState.setHiLoUnlocked(true);
      void stateServices.sessionState.setHiLoEnvironment(env || null);
      console.log("[hilo] Unlocked to environment:", env);
      return env;
    } catch (e: any) {
      const errorMessage = e.message || String(e);
      if (errorMessage.includes("Destroy")) {
        console.warn("[hilo] DESTROY CODE ENTERED - purging HI data and switching to LO");
        try {
          const current = getHiLoState();
          const serialized = JSON.parse(current.toJSON());
          serialized.environment = "lo";
          serialized.is_locked = false;
          hiloStateInstance = HiLoState.fromJSON(JSON.stringify(serialized));
        } catch (stateError) {
          console.warn("[hilo] Failed to rewrite state for destroy flow:", stateError);
        }

        void persistHiLoState();
        set((state) => {
          state.isLocked = false;
          state.isConfigured = true;
          state.environment = "lo";
        });
        void stateServices.sessionState.setHiLoEnabled(true);
        void stateServices.sessionState.setHiLoUnlocked(true);
        void stateServices.sessionState.setHiLoEnvironment("lo");

        // Purge HI data asynchronously, then reload to clear any in-memory HI content.
        (async () => {
          try {
            await storage.destroyAllHiData();
          } catch (purgeError) {
            console.error("[hilo] Failed to purge HI data:", purgeError);
          } finally {
            window.location.reload();
          }
        })();
        return null;
      }
      console.warn("[hilo] Invalid code");
      return null;
    }
  },

  shouldLock: () => {
    // This should only be called after loadCodes which initializes WASM
    if (!hiloStateInstance) {
      return false;
    }
    return hiloStateInstance.shouldLock();
  },
});
